import React, { useState } from "react";
import { db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { motion } from "framer-motion";
import { FiPhone, FiMail, FiMapPin } from "react-icons/fi";
import { SiLinkedin } from "react-icons/si";

const container = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
      when: "beforeChildren",
      staggerChildren: 0.12,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

// Update these with your real details
const CONTACT = {
  phone: "+880 (1744) 353576",
  email: "fahimferdous.s@outlook.com",
  linkedin: "linkedin.com/in/fahimferdous",
  address: "Mirpur, Dhaka, Bangladesh",
};

function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [errors, setErrors] = useState({});

  const validate = (data) => {
    const errs = {};
    if (!data.name || data.name.trim().length < 2)
      errs.name = "Please enter your full name.";
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      errs.email = "Enter a valid email.";
    if (!data.phone || data.phone.trim().length < 6)
      errs.phone = "Enter a valid phone number.";
    if (!data.interest) errs.interest = "Select what you're interested in.";
    if (!data.budget) errs.budget = "Select a budget range.";
    if (!data.country || data.country.trim().length < 2)
      errs.country = "Enter your country.";
    if (!data.message || data.message.trim().length < 10)
      errs.message = "Please provide a brief message (10+ chars).";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    const v = validate(data);
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    try {
      setSubmitting(true);
      await addDoc(collection(db, "contactLeads"), {
        name: data.name,
        email: data.email,
        phone: data.phone,
        interest: data.interest,
        budget: data.budget,
        country: data.country,
        message: data.message,
        source: "home_contact_section",
        createdAt: serverTimestamp(),
      });
      setSuccess("Thanks! Your message was sent successfully.");
      e.currentTarget.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.3 }}
          className="flex items-center justify-center mb-8 sm:mb-10"
        >
          <motion.div variants={item} className="space-y-2 text-center">
            <p className="text-sm font-medium text-gray-500 flex items-center gap-2 justify-center">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: "rgb(232, 51, 16)" }}
              />
              Contact Us
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
              Let's Talk for{" "}
              <span style={{ color: "rgb(232, 51, 16)" }}>
                Your Next Projects
              </span>
            </h2>
          </motion.div>
        </motion.div>

        {/* Layout */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Left info */}
          <motion.div variants={item} className="space-y-5">
            <p className="text-gray-600 max-w-md">
              Let me know what you’re looking to build. I usually reply within
              24 hours.
            </p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#e83310] text-white">
                  <FiPhone />
                </span>
                <a href={`tel:${CONTACT.phone}`} className="text-gray-800">
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#e83310] text-white">
                  <FiMail />
                </span>
                <a href={`mailto:${CONTACT.email}`} className="text-gray-800">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#e83310] text-white">
                  <SiLinkedin />
                </span>
                <span className="text-gray-800">{CONTACT.linkedin}</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#e83310] text-white">
                  <FiMapPin />
                </span>
                <span className="text-gray-800">{CONTACT.address}</span>
              </li>
            </ul>
          </motion.div>

          {/* Right form */}
          <motion.form
            variants={item}
            onSubmit={onSubmit}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-2xl p-5 border border-gray-200"
          >
            {success && (
              <div className="sm:col-span-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                {success}
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Your Name *
              </label>
              <input
                name="name"
                className={`w-full rounded-lg border ${
                  errors.name ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
                placeholder="Ex. John Doe"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                name="email"
                className={`w-full rounded-lg border ${
                  errors.email ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
                placeholder="example@gmail.com"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Phone *
              </label>
              <input
                name="phone"
                className={`w-full rounded-lg border ${
                  errors.phone ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
                placeholder="Enter Phone Number"
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                I’m Interested in *
              </label>
              <select
                name="interest"
                className={`w-full rounded-lg border ${
                  errors.interest ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
              >
                <option value="">Select</option>
                <option>Website</option>
                <option>Web App</option>
                <option>Mobile App</option>
                <option>UI/UX Design</option>
                <option>Consultation</option>
              </select>
              {errors.interest && (
                <p className="mt-1 text-xs text-red-600">{errors.interest}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Budget Range (USD) *
              </label>
              <select
                name="budget"
                className={`w-full rounded-lg border ${
                  errors.budget ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
              >
                <option value="">Select Range</option>
                <option>$500 – $1,000</option>
                <option>$1,000 – $3,000</option>
                <option>$3,000 – $10,000</option>
                <option>$10,000+</option>
              </select>
              {errors.budget && (
                <p className="mt-1 text-xs text-red-600">{errors.budget}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Country *
              </label>
              <input
                name="country"
                className={`w-full rounded-lg border ${
                  errors.country ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
                placeholder="Select Country"
              />
              {errors.country && (
                <p className="mt-1 text-xs text-red-600">{errors.country}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Your Message *
              </label>
              <textarea
                name="message"
                rows={5}
                className={`w-full rounded-lg border ${
                  errors.message ? "border-red-400" : "border-gray-300"
                } bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400`}
                placeholder="Enter here.."
              />
              {errors.message && (
                <p className="mt-1 text-xs text-red-600">{errors.message}</p>
              )}
            </div>
            <div className="sm:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 font-semibold bg-[#211033] text-white border border-teal-700 rounded-full py-2 px-5 hover:bg-white hover:text-[#211033] ease-in duration-300 shadow disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
}

export default ContactSection;
